/**
 * Werte und Texte der Beleg-Engine (Story 3.1), ohne Datenbank: Belegarten, Nummernformat, Geld- und Datumsformat.
 * Abnahme durch Steuerberater/Rudolf nötig (Nummernformat, Pflichtangaben).
 */
import { formatMoneyDe } from '@till-erp/platform/currencies';

/**
 * Belegarten der Engine. Welchen Nummernkreis eine Belegart nutzt, steht in der Einstellung `document_number_ranges`
 * (Story 3.2, AD-9); ausstellen und Vorlagen anmelden tun die Besitzer-Epics.
 */
export const documentKinds = ['sample', 'invoice', 'invoice_correction', 'credit_note', 'cancellation', 'proforma', 'delivery_note', 'purchase_order', 'order_confirmation', 'quote'] as const;
export type DocumentKind = (typeof documentKinds)[number];

export const documentKindLabels: Record<DocumentKind, string> = {
  sample: 'Muster',
  invoice: 'Rechnung',
  invoice_correction: 'Berichtigung',
  credit_note: 'Gutschrift',
  cancellation: 'Stornorechnung',
  proforma: 'Proforma',
  delivery_note: 'Lieferschein',
  purchase_order: 'Lieferantenbestellung',
  // Besitzer orders (epic-auftragseingang Story 11): Auswahl der Einstellungen kennt sie auch ohne geladenes Modul orders.
  order_confirmation: 'Auftragsbestätigung',
  // Besitzer quotes (epic-auftraege-im-erp Story 9.8).
  quote: 'Angebot',
};

/**
 * Nummernkreise mit Beschriftung (Story 3.7, FR-39), in der Reihenfolge der Seite Einstellungen › Nummernkreise. Die
 * Kreise selbst (Format, Zähler) stehen in `documents.number_range`; unbekannte Kreise zeigt die Seite mit ihrem Schlüssel.
 */
export const numberRangeLabels: Record<string, string> = {
  invoice: 'Rechnung',
  credit_note: 'Gutschrift',
  proforma: 'Proforma',
  delivery_note: 'Lieferschein',
  sales_order: 'Auftrag',
  purchase_order: 'Lieferantenbestellung',
  quote: 'Angebot',
  sample: 'Muster',
};
export const numberRangeOrder = Object.keys(numberRangeLabels);

/** Name eines Kreises für Meldungen (invoice → „Rechnung“); unbekannt → Schlüssel. */
export function numberRangeLabel(rangeKey: string): string {
  return numberRangeLabels[rangeKey] ?? rangeKey;
}

/** Name des Abgleich-Schritts nach einer Wiederherstellung (nie umbenennen, Fortschritt hängt daran). */
export const NUMBER_RANGE_RESTORE_STEP = 'documents.numberRanges';

/** Grund einer Lücke (`documents.number_gap.reason_code`). */
export const numberGapReasonCodes = ['taken_over', 'manual', 'restore'] as const;
export type NumberGapReasonCode = (typeof numberGapReasonCodes)[number];

/** Gründe, mit denen Admin oder Umschalt-Import einen Zählerstand setzen (Story 3.7). */
export const counterSetReasons = ['taken_over', 'manual'] as const;
export type CounterSetReason = (typeof counterSetReasons)[number];
export const counterSetReasonLabels: Record<CounterSetReason, string> = {
  taken_over: 'Übernahme aus Xentral',
  manual: 'Sprung von Hand',
};

/**
 * Musterfälle zur Auswahl auf Einstellungen › Belegvorlagen (Story 3.3, 3.8): goldene Fälle R-01 (Netto-Modus), R-02
 * (Brutto-Modus), R-09 (EU-Privatkunde Italien, OSS 22 %, italienischer Produkttext) und R-11 (Ausfuhr Schweiz in CHF
 * mit Kurs). Die Daten stehen in `sample.ts`.
 */
export const SAMPLE_CASES = ['R-01', 'R-02', 'R-09', 'R-11'] as const;
export type SampleCase = (typeof SAMPLE_CASES)[number];
/** Ohne Auswahl (bisheriges Verhalten, REST abwärtskompatibel). */
export const DEFAULT_SAMPLE_CASE: SampleCase = 'R-01';
export const sampleCaseLabels: Record<SampleCase, string> = {
  'R-01': 'R-01 · Netto-Modus',
  'R-02': 'R-02 · Brutto-Modus',
  'R-09': 'R-09 · Italien, OSS 22 %',
  'R-11': 'R-11 · Schweiz in CHF',
};
/** Belegsprachen der Muster (Story 3.8); ohne Auswahl Deutsch. */
export const SAMPLE_LANGUAGES = ['de', 'en'] as const;
export type SampleLanguage = (typeof SAMPLE_LANGUAGES)[number];
export const DEFAULT_SAMPLE_LANGUAGE: SampleLanguage = 'de';
export const sampleLanguageLabels: Record<SampleLanguage, string> = { de: 'Deutsch', en: 'Englisch' };

/** Aufdruck auf jedem Muster: Muster gehen nie in Versand, DATEV oder Zahlungsabgleich. */
export const SAMPLE_WATERMARK = 'MUSTER – kein gültiger Beleg';

/**
 * Platzhalter der Fußzeile (Einstellung „Fußzeile der Belege“, Story 3.4) und das Feld des Briefkopfs im Snapshot, aus
 * dem sie beim Rendern gefüllt werden (Werte aus Einstellungen › Firma zum Zeitpunkt der Ausstellung).
 */
export const DOCUMENT_FOOTER_FIELDS = {
  Firmenname: 'name',
  Anschrift: 'address',
  Kontakt: 'contact',
  Bankverbindung: 'bank',
  Registerangaben: 'register',
  'USt-ID': 'vatId',
} as const;
export type DocumentFooterPlaceholder = keyof typeof DOCUMENT_FOOTER_FIELDS;

/** Kennung der Einstellungsgruppe „Belege“ (auch Adresse `/einstellungen/documents`). */
export const DOCUMENTS_SETTINGS_GROUP = 'documents';

// Story 27.2: Etiketten (AD-42) – Prüfung beim Speichern, Startvorlagen, Größen und Texte, browser-sicher. Das
// gemeinsame Design-Schema (`core/labels/design.ts`) exportiert Story 27.3.
export * from './core/labels/check.ts';
export * from './core/labels/starters.ts';
export * from './labels/texts.ts';

/** Platzhalter in einem Text der Fußzeile, die es nicht gibt (z. B. Tippfehler `{Kontak}`). */
export function unknownFooterPlaceholders(text: string): string[] {
  return [...text.matchAll(/\{([^}]*)\}/g)].map((m) => m[1]!).filter((name) => !Object.hasOwn(DOCUMENT_FOOTER_FIELDS, name));
}
export const DOCUMENT_FOOTER_PLACEHOLDERS = Object.keys(DOCUMENT_FOOTER_FIELDS) as DocumentFooterPlaceholder[];

export const DOCUMENT_TEXTS = {
  sampleForbidden: 'Nur Admins dürfen ein Muster erzeugen.',
  sampleCaseUnknown: 'Diesen Musterfall gibt es nicht. Bitte „R-01“ (Netto-Modus), „R-02“ (Brutto-Modus), „R-09“ (Italien) oder „R-11“ (Schweiz in CHF) wählen.',
  sampleLanguageUnknown: 'Diese Belegsprache gibt es nicht. Bitte „de“ (Deutsch) oder „en“ (Englisch) wählen.',
  sampleBodyNotJson: 'Der Inhalt muss JSON sein (Content-Type „application/json“), z. B. {"case":"R-02"}. Es wurde nichts ausgestellt.',
  templateUnknown: (key: string, version: string) =>
    `Die Belegvorlage „${key}“ in der Fassung „${version}“ ist unbekannt; es wurde kein PDF erzeugt. Bitte den Betrieb informieren.`,
  rangeMissing: 'Für diese Belegart gibt es keinen Nummernkreis. Bitte den Betrieb informieren.',
  numberTaken: (number: string) =>
    `Die Nummer ${number} ist schon an einen Beleg aus einem anderen Nummernkreis vergeben; es wurde nichts ausgestellt. Bitte den Betrieb informieren (Format der Nummernkreise prüfen).`,
  rangeFull: (range: string, last: string) =>
    `Nummernkreis voll: Im Kreis „${range}“ ist ${last} die letzte Nummer mit der eingestellten Stellenzahl. Es wurde nichts ausgestellt; bitte die Stellenzahl unter Einstellungen › Belege erhöhen lassen.`,
  rangeFullCaseTitle: (range: string) => `Nummernkreis „${range}“ ist voll`,
  rangeFullCaseDetail: (range: string, last: string) =>
    `Im Nummernkreis „${range}“ wurde die letzte mögliche Nummer ${last} vergeben. Weitere Belege dieses Kreises werden abgelehnt, bis die Stellenzahl erhöht ist (Einstellungen › Belege) oder das neue Jahr beginnt.`,
  digitsMissing: 'Für diesen Nummernkreis ist keine Stellenzahl eingestellt (Einstellung „Stellenzahl je Nummernkreis“). Bitte den Betrieb informieren.',
  yearBehind: (year: string, rangeYear: number) =>
    `Das Belegdatum liegt im Jahr ${year}, der Nummernkreis zählt schon im Jahr ${rangeYear}. Es wurde nichts ausgestellt.`,
  assignmentChanged: 'Die Zuordnung der Nummernkreise hat sich gerade geändert. Bitte noch einmal versuchen.',
  /** Story 12.1: Belegdatum (z. B. Versandtag des Tagesabschlusses) liegt nach heute. */
  dateInFuture: 'Das Belegdatum liegt in der Zukunft. Ein Beleg bekommt höchstens das heutige Datum.',
  dateInvalid: 'Das Belegdatum muss ein Datum im Format JJJJ-MM-TT sein.',
  counterNumberMissing: 'Bitte die zuletzt vergebene Nummer eingeben.',
  counterChanged: 'Jemand anderes hat den Zählerstand inzwischen geändert. Bitte die Seite neu laden und noch einmal prüfen.',
  counterForbidden: 'Nur Admins dürfen einen Zählerstand setzen.',
  counterBelowHighest: (highest: string) => `Der Zählerstand darf nicht unter der höchsten vergebenen Nummer ${highest} liegen.`,
  counterFormat: (range: string, example: string) =>
    `Die Nummer passt nicht zum Format des Kreises „${range}“ (z. B. ${example}). Bitte die zuletzt vergebene Nummer genau so eingeben.`,
  counterYear: (year: string) => `Die Nummer muss im laufenden Jahr ${year} liegen.`,
  counterYearBehind: (rangeYear: number) => `Der Nummernkreis zählt schon im Jahr ${rangeYear}; ein Zählerstand für ein früheres Jahr lässt sich nicht setzen.`,
  restorePending:
    'Nach der Wiederherstellung sind die Nummernkreise noch nicht mit dem Archiv abgeglichen. Bis der Abgleich fertig ist, wird kein Beleg ausgestellt (sonst könnte eine Nummer doppelt vergeben werden).',
  counterNoteRequired: 'Bitte einen Grund für den Sprung angeben (er steht im Prüfbericht).',
  takeoverAfterIssue: (range: string) =>
    `Im Kreis „${range}“ hat das ERP in diesem Jahr schon Belege ausgestellt. Eine Übernahme aus Xentral geht nicht mehr; bitte „Sprung von Hand“ mit Grund wählen.`,
  rangeUnknown: 'Diesen Nummernkreis gibt es nicht.',
  takenOverReason: 'Vor dem Umschalttag in Xentral vergeben',
  restoreGapReason: 'Nach Wiederherstellung über die höchste archivierte Nummer gehoben',
  restoreStepLabel: 'Nummernkreise',
  restoreArchiveMissing: 'Das Archiv ist auf diesem Worker nicht eingerichtet; die Nummernkreise konnten nicht mit dem Archiv abgeglichen werden.',
  restoreSubject: (range: string) => `Nummernkreis ${range}`,
  restoreRaised: (range: string, first: string, last: string) =>
    `Im Archiv liegen Belege des Kreises „${range}“ bis ${last}, die die wiederhergestellte Datenbank nicht kennt. Der Kreis wurde über die höchste archivierte Nummer gehoben; die Nummern ${first} bis ${last} stehen als Lücke im Prüfbericht. Bitte die Belege im Archiv prüfen.`,
  restoreUnclear: (count: number, examples: string[]) =>
    `${count} ${count === 1 ? 'Datei' : 'Dateien'} im Archiv ließen sich keinem Nummernkreis sicher zuordnen (z. B. ${examples.join(', ')}). Die übrigen Kreise sind gehoben; Belege werden erst wieder ausgestellt, wenn das geklärt ist. Bitte den Betrieb informieren (Handbuch Wiederherstellung).`,
  footerPlaceholderUnknown: (names: string[]) =>
    `Platzhalter ${names.map((n) => `{${n}}`).join(', ')} gibt es nicht. Erlaubt sind ${Object.keys(DOCUMENT_FOOTER_FIELDS).map((n) => `{${n}}`).join(', ')}.`,
  footerEmpty: 'Die Fußzeile braucht mindestens eine Zeile.',
  kindNotRegistered: (kind: string) =>
    `Die Belegart „${kind}“ ist bei der Beleg-Engine nicht angemeldet; es wurde nichts ausgestellt. Bitte den Betrieb informieren.`,
  rangeNotAssigned: 'Dieser Belegart ist kein Nummernkreis zugeordnet (Einstellung „Nummernkreis je Belegart“). Bitte den Betrieb informieren.',
  rendering: 'wird erzeugt …',
  sealed: 'im Archiv',
  invalid: 'ungültig',
  versionMissing: 'Diese Belegfassung gibt es nicht.',
  pdfMissing: 'Das PDF ist noch nicht im Archiv.',
  pdfChecksum: 'Die Prüfsumme des PDF im Archiv stimmt nicht. Bitte den Betrieb informieren; das PDF wird nicht ausgeliefert.',
  pdfLost: 'Das PDF fehlt im Archiv, obwohl es eingetragen ist. Bitte den Betrieb informieren.',
  /** Story 12.1: XML der E-Rechnung oder Prüfbericht. */
  artifactMissing: 'Diese Datei gibt es zu dieser Fassung nicht (nur bei E-Rechnungen, sobald sie im Archiv ist).',
  artifactChecksum: 'Die Prüfsumme der Datei im Archiv stimmt nicht. Bitte den Betrieb informieren; die Datei wird nicht ausgeliefert.',
  artifactLost: 'Die Datei fehlt im Archiv, obwohl sie eingetragen ist. Bitte den Betrieb informieren.',
  purgedNoArtifact: 'Der Inhalt dieser Fassung wurde nach Fristende entfernt; es gibt die Datei nicht mehr. Nummer, Datum und Prüfsumme bleiben.',
  archiveNotConfigured: 'Das Archiv ist auf diesem Server nicht eingerichtet; das PDF kann nicht gelesen werden.',
  archiveUnavailable: 'Das Archiv ist gerade nicht erreichbar. Bitte gleich noch einmal versuchen.',
  // Story 3.5: neue Fassung derselben Nummer (documents.revise, documents.reviseSample).
  documentMissing: 'Diesen Beleg gibt es nicht.',
  documentChanged: 'Inzwischen gibt es eine neuere Fassung dieses Belegs. Bitte die Seite neu laden und die Änderung noch einmal eintragen.',
  documentVersionRendering: 'Die jüngste Fassung wird gerade erzeugt. Bitte warten, bis sie im Archiv liegt, und dann noch einmal speichern.',
  reviseSampleForbidden: 'Nur Admins dürfen ein Muster bearbeiten.',
  reviseNotSample: 'Hier lassen sich nur Muster bearbeiten.',
  recipientLinesRequired: 'Bitte mindestens den Firmennamen des Empfängers angeben.',
  revisionUnchanged: 'Keine Änderung – es wurde keine neue Fassung angelegt.',
  revisionCreated: (versionNo: number) => `Fassung ${versionNo} angelegt. Das PDF wird erzeugt.`,
  // Story 3.6: ungültige Fassung, Klärfall und neue Fassung derselben Nummer (documents.recreateVersion).
  invalidCaseTitle: (number: string) => `Beleg ${number} konnte nicht erzeugt werden`,
  invalidCaseDetail: (reason: string, versionNo: number, recovery: 'auto' | 'manual') => {
    const tail =
      recovery === 'auto'
        ? `Fassung ${versionNo} ist ungültig; sobald PDF-Dienst und Archiv wieder erreichbar sind, legt das ERP eine neue Fassung derselben Nummer an. Versendet wird erst, wenn sie im Archiv liegt.`
        : `Fassung ${versionNo} ist ungültig; bitte die Ursache beheben lassen und danach mit „Neu erzeugen“ eine neue Fassung derselben Nummer anlegen. Versendet wird erst, wenn sie im Archiv liegt.`;
    // Klärfall-Satz höchstens 500 Zeichen: notfalls den Grund kürzen, nie den Hinweis.
    const room = 500 - tail.length - 1;
    const head = reason.length > room ? `${reason.slice(0, Math.max(0, room - 1)).trimEnd()}…` : reason;
    return `${head} ${tail}`;
  },
  invalidCaseResolved: (versionNo: number) => `Fassung ${versionNo} erzeugt und im Archiv.`,
  // Story 12.2: XRechnung ohne Käuferreferenz (Leitweg-ID) – ein Klärfall je Rechnung, Bereich Buchhaltung.
  buyerReferenceCaseTitle: (number: string) => `E-Rechnung: Käuferreferenz fehlt (Rechnung ${number})`,
  buyerReferenceCaseDetail: (versionNo: number) =>
    `Der Kunde bekommt eine XRechnung, aber am Kunden ist keine Leitweg-ID (Käuferreferenz) eingetragen. Fassung ${versionNo} ist ungültig und wird nicht versendet. Sobald die Leitweg-ID am Kunden eingetragen ist, legt das ERP selbst eine neue Fassung derselben Nummer an und versendet sie nach bestandener Prüfung.`,
  buyerReferenceReason: 'E-Rechnung: Käuferreferenz (Leitweg-ID) fehlt.',
  invalidNextStepBuyerReference: 'Nächster Schritt: Leitweg-ID am Kunden eintragen – danach entsteht automatisch eine neue Fassung derselben Nummer und wird versendet.',
  recreateForbidden: 'Nur Admins dürfen eine Belegfassung neu erzeugen.',
  versionNotInvalid: 'Die jüngste Fassung dieses Belegs ist nicht ungültig; eine neue Fassung ist nicht nötig.',
  // Story 3.9: Aufbewahrungsfrist und Entfernen nach Fristende (documents.purgeExpired).
  retentionMissing: (kindLabel: string) =>
    `Für die Belegart „${kindLabel}“ ist keine Aufbewahrungsfrist eingestellt (Einstellung „Aufbewahrungsfrist je Belegart“). Es wurde nichts ausgestellt; bitte den Betrieb informieren.`,
  retentionRunning: (retainUntil: string) => `Aufbewahrungsfrist läuft bis ${formatDate(retainUntil)}.`,
  purgeRendering: 'Eine Fassung dieses Belegs wird gerade erzeugt; der Beleg kann nicht entfernt werden.',
  pricelessNotRevisable: 'Ein Lieferschein lässt sich nicht überarbeiten; für eine weitere Lieferung entsteht ein neuer Lieferschein.',
  documentPurged: 'Dieser Beleg wurde nach Ablauf der Aufbewahrungsfrist entfernt; eine neue Fassung ist nicht mehr möglich.',
  // Story 11.25: Belegart mit `afterIssue: 'void'` (Lieferschein) – nie neue Fassung, nur Vermerk „ungültig“.
  kindNotRevisable: (kindLabel: string) =>
    `Ein ${kindLabel} lässt sich nach dem Ausstellen nicht überarbeiten. Bitte ihn ungültig ablegen und neu ausstellen.`,
  kindNotVoidable: (kindLabel: string) => `Die Belegart „${kindLabel}“ lässt sich nicht ungültig ablegen; sie wird überarbeitet (neue Fassung).`,
  alreadyVoid: 'Dieser Beleg ist schon ungültig abgelegt.',
  voidReasonMissing: 'Bitte einen Grund angeben (höchstens 500 Zeichen).',
  voided: 'ungültig',
  // Oberfläche Story 3.6/3.9 (Beleg-Ansicht, Einstellungen › Belegvorlagen). Abnahme durch Steuerberater/Rudolf nötig.
  pendingSend: 'Wartet auf Erzeugung – wird noch nicht versendet.',
  purgedNeverSend: 'Nach Fristende entfernt – wird nie versendet.',
  invalidTitle: (versionNo: number) => `Fassung ${versionNo} ist ungültig`,
  invalidAttempts: (attempts: number) => `${attempts} ${attempts === 1 ? 'Versuch' : 'Versuche'} gescheitert.`,
  invalidReasonMissing: 'Ohne Grund.',
  invalidNextStepAuto: 'Nächster Schritt: Das ERP legt eine neue Fassung derselben Nummer an, sobald PDF-Dienst und Archiv wieder erreichbar sind.',
  invalidNextStepAdmin: 'Nächster Schritt: Ursache beheben lassen und danach mit „Neu erzeugen“ eine neue Fassung derselben Nummer anlegen.',
  invalidNextStepAdminElsewhere: 'Nächster Schritt: Ursache beheben lassen und danach in der Beleg-Ansicht („Beleg öffnen“) mit „Neu erzeugen“ eine neue Fassung derselben Nummer anlegen.',
  invalidNextStepUser: 'Nächster Schritt: Ein Admin legt nach Behebung der Ursache mit „Neu erzeugen“ eine neue Fassung derselben Nummer an.',
  recreate: 'Neu erzeugen',
  recreateAutoHint: 'Das ERP versucht es selbst; Sie können auch jetzt neu erzeugen.',
  recreateHint: (versionNo: number) => `Legt Fassung ${versionNo} mit derselben Nummer und denselben Belegdaten an; die ungültige Fassung bleibt sichtbar.`,
  recreated: (versionNo: number) => `Fassung ${versionNo} angelegt. Das PDF wird erzeugt.`,
  invalidNoPdf: 'Diese Fassung ist ungültig; es gibt kein PDF.',
  retainUntilLabel: 'Aufzubewahren bis',
  purgedLabel: 'nach Fristende entfernt',
  purgedNoPdf: 'Der Inhalt dieser Fassung wurde nach Fristende entfernt; es gibt kein PDF mehr. Nummer, Datum und Prüfsumme bleiben.',
  retentionTitle: 'Aufbewahrungsfristen',
  retentionNote:
    'Jede Fassung eines Belegs wird bis zum 31.12. des Belegjahrs plus dieser Frist aufbewahrt; bis dahin lässt sich nichts ändern oder löschen. Die Fristen werden nur nach Freigabe mit dem Steuerberater geändert. Abnahme durch Steuerberater/Rudolf nötig.',
  retentionSettingMissing: 'Die Aufbewahrungsfristen sind nicht eingestellt (Einstellung „Aufbewahrungsfrist je Belegart“). Bitte den Betrieb informieren.',
  // Ticket 13 (Fund B1): Summen beim Ausstellen und Überarbeiten mit computeDocumentTotals nachrechnen.
  totalsVersion: (expected: string) =>
    `Die Summen stammen aus einer anderen Fassung der Summenrechnung; erwartet ist „${expected}“. Bitte die Summen neu rechnen lassen.`,
  totalsMismatch: (label: string, given: string, computed: string) => `${label} passt nicht zu den Positionen: angegeben ${given}, gerechnet ${computed}.`,
  totalsBreakdownMismatch: (computed: string) =>
    `Die Aufschlüsselung der Umsatzsteuer stimmt nicht mit der Nachrechnung überein; gerechnet: ${computed}.`,
  totalsNotComputable: (reason: string) => `Die Summen lassen sich nicht nachrechnen: ${reason}`,
  totalsMismatchDetail:
    'Die Summen des Belegs passen nicht zu den Positionen. Es wurde nichts gespeichert. Bitte den Betrieb informieren.',
} as const;

const COUNTER_PLACEHOLDER = '{ZAEHLER}';
const YEAR_PLACEHOLDER = '{JAHR}';
const ANY_PLACEHOLDER = /\{[^}]*\}/g;

/** Platzhalter eines Formats prüfen: genau einmal `{ZAEHLER}`, höchstens einmal `{JAHR}`, sonst nichts. */
function placeholdersOf(pattern: string): { hasYear: boolean } {
  const placeholders = pattern.match(ANY_PLACEHOLDER) ?? [];
  const counters = placeholders.filter((p) => p === COUNTER_PLACEHOLDER).length;
  const years = placeholders.filter((p) => p === YEAR_PLACEHOLDER).length;
  if (counters !== 1 || years > 1 || counters + years !== placeholders.length) throw new Error(`Nummernformat ungültig: ${pattern}`);
  return { hasYear: years === 1 };
}

/** Beginnt der Zähler je Jahr neu? Ja, wenn das Format `{JAHR}` enthält (Rudolf 04.10.2026). */
export function patternHasYear(pattern: string): boolean {
  return placeholdersOf(pattern).hasYear;
}

/** Höchster Zählerstand bei `digits` Stellen (4 → 9999); darüber ist der Kreis voll. */
export function maxCounter(digits: number): number {
  if (!Number.isSafeInteger(digits) || digits < 1 || digits > 12) throw new Error(`Stellenzahl ungültig: ${digits}`);
  return 10 ** digits - 1;
}

/**
 * Nummer aus dem Format eines Kreises (wie in Xentral, AD-9): `{JAHR}` = Jahr des Belegdatums, `{ZAEHLER}` = Zähler
 * mit genau `digits` Stellen (führende Nullen). Beispiele: `{JAHR}{ZAEHLER}`, 8101, 4 Stellen, 2027-02-02 → `20278101`;
 * `{JAHR}70{ZAEHLER}`, 301, 3 → `202770301`; `MUSTER-{ZAEHLER}`, 1, 4 → `MUSTER-0001`. Ein Zähler mit mehr Stellen
 * wird nie gekürzt oder umgebrochen, sondern ist ein Fehler (Kreis voll). Andere oder doppelte Platzhalter auch.
 */
export function formatDocumentNumber(pattern: string, counter: number, digits: number, date: string): string {
  placeholdersOf(pattern);
  if (!Number.isSafeInteger(counter) || counter < 1) throw new Error(`Zählerstand ungültig: ${counter}`);
  if (counter > maxCounter(digits)) throw new Error(`Zählerstand ${counter} hat mehr als ${digits} Stellen.`);
  const year = /^(\d{4})-\d{2}-\d{2}$/.exec(date)?.[1];
  if (!year) throw new Error(`Belegdatum ungültig: ${date}`);
  return pattern.replace(YEAR_PLACEHOLDER, year).replace(COUNTER_PLACEHOLDER, String(counter).padStart(digits, '0'));
}

/**
 * Gegenstück zu `formatDocumentNumber`: zerlegt eine Nummer nach dem Format eines Kreises in Jahr und Zähler.
 * `digits` = genau so viele Stellen (Eingabe am Umschalttag); `null` = beliebig viele (Archiv: die Stellenzahl kann sich
 * seit der Ablage geändert haben). Passt die Nummer nicht → `null`.
 */
export function parseDocumentNumber(pattern: string, number: string, digits: number | null): { year: number | null; counter: number } | null {
  const { hasYear } = placeholdersOf(pattern);
  const escape = (text: string) => text.replace(/[.*+?^$()|[\]\\]/g, '\\$&');
  if (digits !== null) maxCounter(digits); // Stellenzahl prüfen
  const counterPart = digits === null ? '(\\d+)' : `(\\d{${digits}})`;
  const parts = pattern.split(/(\{JAHR\}|\{ZAEHLER\})/);
  const order: ('year' | 'counter')[] = [];
  const source = parts
    .map((part) => {
      if (part === YEAR_PLACEHOLDER) {
        order.push('year');
        return '(\\d{4})';
      }
      if (part === COUNTER_PLACEHOLDER) {
        order.push('counter');
        return counterPart;
      }
      return escape(part);
    })
    .join('');
  const match = new RegExp(`^${source}$`).exec(number);
  if (!match) return null;
  const year = hasYear ? Number(match[order.indexOf('year') + 1]) : null;
  const counter = Number(match[order.indexOf('counter') + 1]);
  if (!Number.isSafeInteger(counter)) return null;
  return { year, counter };
}

/**
 * Betrag in kleinster Einheit (als Text oder bigint) deutsch formatiert, z. B. `24804` → `248,04 €`, ISK `12345` →
 * `12.345 ISK`. Nachkommastellen nach ISO 4217 aus platform (der Snapshot trägt sie mit, `computeDocumentTotals` prüft sie).
 */
export function formatMoney(minor: string | bigint, currency: { code: string; minorUnits: number }): string {
  return formatMoneyDe(typeof minor === 'bigint' ? minor : BigInt(minor), currency.code);
}

/** Dezimaltext mit Punkt deutsch, ohne überflüssige Nullen hinter 2 Stellen: `38.9000` → `38,90`, `2` → `2`. */
export function formatDecimal(value: string, minFraction = 0): string {
  const [int = '0', frac = ''] = value.split('.');
  let f = frac.replace(/0+$/, '');
  if (f.length < minFraction) f = f.padEnd(minFraction, '0');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return f ? `${grouped},${f}` : grouped;
}

/** Fachliches Datum `YYYY-MM-DD` deutsch: `02.02.2027`. */
export function formatDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d}.${m}.${y}`;
}

/** Pfad im Archiv ohne Personendaten, z. B. `documents/sample/MUSTER-0001-v1.pdf` (Probe-Nummern tragen `P-`). */
export function documentArchivePath(input: { kind: string; number: string; versionNo: number; extension: string }): string {
  const safe = input.number.replace(/[^A-Za-z0-9._-]/g, '_');
  return `documents/${input.kind}/${safe}-v${input.versionNo}.${input.extension}`;
}

// Story 27.3: Vertrag der Etiketten-Vorlage (Schema, Typen, Version) – leicht, ohne bwip-js/pdf-lib im Seiten-Bundle.
export {
  LABEL_RENDER_VERSION,
  labelBarcodeFormats,
  labelBarcodeKind,
  labelQrEccLevels,
  labelDesignSchema,
  labelElementSchema,
  labelFontFamilies,
  labelSegmentSchema,
  labelSheetLayoutSchema,
  type LabelBarcodeFormat,
  type LabelBarcodeKind,
  type LabelQrEcc,
  type LabelDesign,
  type LabelDesignInput,
  type LabelElement,
  type LabelFontFamily,
  type LabelSegment,
  type LabelSheetLayout,
} from './core/labels/design.ts';

// Story 27.5: Rechte-Tabelle der Etiketten (eine Stelle für Server und Web-App) und die Formatierung der Feldwerte für
// Vorschau und Druck – browser-sicher.
export { hasLabelPermission, hasLabelRight, LABEL_ACTORS, LABEL_RIGHTS, labelFieldVisible, type LabelRight } from './labels/access.ts';
export { formatLabelFieldValue, type LabelFieldColumn } from './core/labels/field-values.ts';
export { parseLabelOffsetMm } from './core/labels/printer.ts';
